import React, { useEffect, useMemo, useState } from 'react';
import { FaSpinner, FaPlus, FaTimes } from 'react-icons/fa';
import { adminVehicleCatalogService, authService } from '../../../../services/apiService';
import { getApiErrorMessage } from '../../../../utils/api';
import { toast } from 'react-toastify';
import VehicleCatalog from './VehicleCatalog';
import MarketplaceProductForm from '../components/MarketplaceProductForm';

const CATALOG_MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const CATALOG_FUEL_OPTIONS = ['', 'Petrol', 'Diesel', 'CNG', 'LPG', 'Electric', 'Hybrid', 'Plug-in hybrid'];

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const commaIndex = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const VehicleManagement = () => {
  const [showAddCatalogModal, setShowAddCatalogModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [catalogRefreshKey, setCatalogRefreshKey] = useState(0);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogUploadingMakerImages, setCatalogUploadingMakerImages] = useState(false);
  const [catalogUploadingModelImages, setCatalogUploadingModelImages] = useState(false);
  const [productMakers, setProductMakers] = useState([]);
  const [productModels, setProductModels] = useState([]);
  const [productTrims, setProductTrims] = useState([]);
  const [productMakerId, setProductMakerId] = useState('');
  const [productModelId, setProductModelId] = useState('');
  const [productGeneration, setProductGeneration] = useState('');
  const [loadingProductMakers, setLoadingProductMakers] = useState(false);
  const [loadingProductModels, setLoadingProductModels] = useState(false);
  const [loadingProductTrims, setLoadingProductTrims] = useState(false);
  const [catalogForm, setCatalogForm] = useState({
    maker: '',
    model: '',
    generation: '',
    modelGenerationYear: '',
    modelVariantLine: '',
    fuelType: '',
    makerImages: [],
    modelImages: [],
    trimGeneration: '',
    year: new Date().getFullYear(),
    month: 1,
    variant: ''
  });

  const openAddProductModal = () => {
    setProductMakerId('');
    setProductModelId('');
    setProductGeneration('');
    setProductModels([]);
    setProductTrims([]);
    setShowAddProductModal(true);
  };

  useEffect(() => {
    if (!showAddProductModal) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingProductMakers(true);
        const data = await adminVehicleCatalogService.listMakers();
        if (!cancelled) setProductMakers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) toast.error(getApiErrorMessage(e, 'Could not load makers'));
      } finally {
        if (!cancelled) setLoadingProductMakers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddProductModal]);

  useEffect(() => {
    if (!showAddProductModal || !productMakerId) {
      setProductModels([]);
      setProductModelId('');
      setProductGeneration('');
      setProductTrims([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingProductModels(true);
        const data = await adminVehicleCatalogService.listModels(productMakerId);
        if (!cancelled) {
          setProductModels(Array.isArray(data) ? data : []);
          setProductModelId('');
          setProductGeneration('');
          setProductTrims([]);
        }
      } catch (e) {
        if (!cancelled) toast.error(getApiErrorMessage(e, 'Could not load models'));
      } finally {
        if (!cancelled) setLoadingProductModels(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddProductModal, productMakerId]);

  useEffect(() => {
    if (!showAddProductModal || !productModelId) {
      setProductTrims([]);
      setProductGeneration('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingProductTrims(true);
        const data = await adminVehicleCatalogService.listTrims(productModelId);
        if (!cancelled) {
          setProductTrims(Array.isArray(data) ? data : []);
          setProductGeneration('');
        }
      } catch (e) {
        if (!cancelled) toast.error(getApiErrorMessage(e, 'Could not load model generations'));
      } finally {
        if (!cancelled) setLoadingProductTrims(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddProductModal, productModelId]);

  const selectedMakerForProduct = useMemo(
    () => productMakers.find((m) => String(m._id) === String(productMakerId)) || null,
    [productMakers, productMakerId]
  );

  const selectedModelForProduct = useMemo(
    () => productModels.find((mo) => String(mo._id) === String(productModelId)) || null,
    [productModels, productModelId]
  );

  const generationOptions = useMemo(() => {
    const unique = new Set();
    const modelGeneration = String(selectedModelForProduct?.generation || '').trim();
    if (modelGeneration) unique.add(modelGeneration);
    (productTrims || [])
      .map((t) => String(t.generation || '').trim())
      .filter(Boolean)
      .forEach((g) => unique.add(g));
    return [...unique];
  }, [productTrims, selectedModelForProduct]);

  useEffect(() => {
    if (!showAddProductModal) return;
    if (generationOptions.length === 1 && !productGeneration) {
      setProductGeneration(generationOptions[0]);
    }
  }, [showAddProductModal, generationOptions, productGeneration]);

  const productVehicleCompatibility = useMemo(() => {
    if (!selectedMakerForProduct || !selectedModelForProduct || !productGeneration) return [];
    const trimsInGeneration = (productTrims || []).filter(
      (t) => String(t.generation || '').trim() === productGeneration
    );
    const years = trimsInGeneration
      .map((t) => Number(t.year))
      .filter((y) => Number.isFinite(y));
    const minYear = years.length ? String(Math.min(...years)) : '';
    const maxYear = years.length ? String(Math.max(...years)) : '';
    return [
      {
        make: String(selectedMakerForProduct.name || '').trim(),
        model: String(selectedModelForProduct.name || '').trim(),
        yearFrom: minYear,
        yearTo: maxYear,
        variant: String(productGeneration || '').trim(),
        fuelType: String(selectedModelForProduct.fuelType || '').trim() || 'All'
      }
    ];
  }, [selectedMakerForProduct, selectedModelForProduct, productGeneration, productTrims]);

  const openAddCatalogModal = () => {
    setCatalogForm({
      maker: '',
      model: '',
      generation: '',
      modelGenerationYear: '',
      modelVariantLine: '',
      fuelType: '',
      makerImages: [],
      modelImages: [],
      trimGeneration: '',
      year: new Date().getFullYear(),
      month: 1,
      variant: ''
    });
    setShowAddCatalogModal(true);
  };

  const submitCatalogVehicle = async (e) => {
    e.preventDefault();
    const makerName = (catalogForm.maker || '').trim();
    const modelName = (catalogForm.model || '').trim();
    const trimGeneration = (catalogForm.trimGeneration || '').trim();
    const variant = (catalogForm.variant || '').trim();
    const makerImageUrls = Array.isArray(catalogForm.makerImages) ? catalogForm.makerImages : [];
    const modelImageUrls = Array.isArray(catalogForm.modelImages) ? catalogForm.modelImages : [];
    if (!makerName || !modelName || !trimGeneration || !variant) {
      toast.error('Car maker, model, trim generation, and variant are required.');
      return;
    }
    const yearNum = Number(catalogForm.year);
    const monthNum = Number(catalogForm.month);
    if (!Number.isFinite(yearNum) || yearNum < 1980 || yearNum > 2100) {
      toast.error('Enter a valid trim year.');
      return;
    }
    const mgyRaw = catalogForm.modelGenerationYear;
    if (mgyRaw !== '' && mgyRaw != null) {
      const gyOpt = Number(mgyRaw);
      if (Number.isFinite(gyOpt) && (gyOpt < 1980 || gyOpt > 2100)) {
        toast.error('Model generation year must be between 1980 and 2100.');
        return;
      }
    }
    try {
      setCatalogSaving(true);
      const makers = await adminVehicleCatalogService.listMakers();
      const listM = Array.isArray(makers) ? makers : [];
      let maker = listM.find(
        (m) => (m.name || '').trim().toLowerCase() === makerName.toLowerCase()
      );
      if (!maker) {
        maker = await adminVehicleCatalogService.createMaker({
          name: makerName,
          imageUrls: makerImageUrls
        });
      } else if (makerImageUrls.length > 0) {
        await adminVehicleCatalogService.updateMaker(maker._id, { imageUrls: makerImageUrls });
      }
      const makerId = maker?._id || maker?.id;
      if (!makerId) {
        throw new Error('Could not resolve car maker.');
      }
      const models = await adminVehicleCatalogService.listModels(makerId);
      const listMo = Array.isArray(models) ? models : [];
      let model = listMo.find(
        (m) => (m.name || '').trim().toLowerCase() === modelName.toLowerCase()
      );
      const modelMeta = {};
      const gen = (catalogForm.generation || '').trim();
      if (gen) modelMeta.generation = gen;
      const mgy = catalogForm.modelGenerationYear;
      if (mgy !== '' && mgy != null) {
        const gy = Number(mgy);
        if (Number.isFinite(gy)) {
          modelMeta.generationYear = gy;
        }
      }
      const mvl = (catalogForm.modelVariantLine || '').trim();
      if (mvl) modelMeta.variant = mvl;
      const ft = (catalogForm.fuelType || '').trim();
      if (ft) modelMeta.fuelType = ft;
      if (modelImageUrls.length > 0) modelMeta.imageUrls = modelImageUrls;

      if (!model) {
        model = await adminVehicleCatalogService.createModel(makerId, {
          name: modelName,
          ...modelMeta
        });
      } else if (Object.keys(modelMeta).length > 0) {
        await adminVehicleCatalogService.updateModel(model._id, modelMeta);
      }
      const modelId = model?._id || model?.id;
      if (!modelId) {
        throw new Error('Could not resolve model.');
      }
      await adminVehicleCatalogService.createTrim(modelId, {
        generation: trimGeneration,
        year: yearNum,
        month: monthNum,
        variant
      });
      toast.success('Catalogue vehicle added.');
      setShowAddCatalogModal(false);
      setCatalogRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add catalogue vehicle.');
    } finally {
      setCatalogSaving(false);
    }
  };

  const uploadCatalogImages = async (filesLike, targetField) => {
    const files = Array.from(filesLike || []);
    if (!files.length) return;
    const setUploading =
      targetField === 'makerImages' ? setCatalogUploadingMakerImages : setCatalogUploadingModelImages;
    try {
      setUploading(true);
      const uploaded = [];
      for (const file of files) {
        const base64 = await readFileAsBase64(file);
        const uploadedUrl = await authService.uploadFile(
          base64,
          file.name,
          file.type || 'image/jpeg'
        );
        uploaded.push(String(uploadedUrl || '').trim());
      }
      setCatalogForm((f) => {
        const existing = Array.isArray(f[targetField]) ? f[targetField] : [];
        const merged = [...new Set([...existing, ...uploaded.filter(Boolean)])];
        return { ...f, [targetField]: merged };
      });
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded.`);
    } catch (err) {
      toast.error(err.message || 'Could not upload image(s).');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {showAddProductModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-marketplace-product-title"
          onClick={() => setShowAddProductModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 id="add-marketplace-product-title" className="text-base font-bold text-gray-900">
                Add marketplace product
              </h3>
              <button
                type="button"
                onClick={() => setShowAddProductModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-600">
                Pick the catalogue <span className="font-semibold">maker</span> and{' '}
                <span className="font-semibold">model</span> so vehicle fitment is filled automatically.
                Categories and subcategories load from your Categories panel.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Car maker
                  </label>
                  <select
                    value={productMakerId}
                    onChange={(e) => setProductMakerId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">— Select maker —</option>
                    {productMakers.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {loadingProductMakers && (
                    <p className="text-[10px] text-gray-500 mt-1">Loading makers…</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Model
                  </label>
                  <select
                    value={productModelId}
                    onChange={(e) => setProductModelId(e.target.value)}
                    disabled={!productMakerId || loadingProductModels}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                  >
                    <option value="">
                      {!productMakerId ? '— Select maker first —' : '— Select model —'}
                    </option>
                    {productModels.map((mo) => (
                      <option key={mo._id} value={mo._id}>
                        {mo.name}
                      </option>
                    ))}
                  </select>
                  {loadingProductModels && (
                    <p className="text-[10px] text-gray-500 mt-1">Loading models…</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Generation
                  </label>
                  <select
                    value={productGeneration}
                    onChange={(e) => setProductGeneration(e.target.value)}
                    disabled={!productModelId || loadingProductTrims}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                  >
                    <option value="">
                      {!productModelId ? '— Select model first —' : '— Select generation —'}
                    </option>
                    {generationOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  {loadingProductTrims && (
                    <p className="text-[10px] text-gray-500 mt-1">Loading generations…</p>
                  )}
                </div>
              </div>
              <MarketplaceProductForm
                compact
                catalogModelId={productModelId}
                vehicleCompatibility={productVehicleCompatibility}
                disableSubmit={!productModelId || !productGeneration}
                onSuccess={() => {
                  setShowAddProductModal(false);
                  setCatalogRefreshKey((k) => k + 1);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showAddCatalogModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-catalog-vehicle-title"
          onClick={() => !catalogSaving && setShowAddCatalogModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 id="add-catalog-vehicle-title" className="text-base font-bold text-gray-900">
                Add vehicle (catalogue)
              </h3>
              <button
                type="button"
                onClick={() => !catalogSaving && setShowAddCatalogModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={submitCatalogVehicle} className="p-5 space-y-4 max-h-[85vh] overflow-y-auto">
              <p className="text-xs text-gray-600">
                Enter maker and model. Optionally set generation, model year, model-line variant, and
                fuel. Then add the catalogue trim row (year, month, variant). Existing makers and
                models are reused when the name matches; optional model fields update a matching
                model.
              </p>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Car maker
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Toyota"
                  value={catalogForm.maker}
                  onChange={(e) => setCatalogForm((f) => ({ ...f, maker: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Model
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Innova Crysta"
                  value={catalogForm.model}
                  onChange={(e) => setCatalogForm((f) => ({ ...f, model: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Maker images (optional)
                </label>
                <label className="inline-flex items-center gap-2 mt-2 text-xs text-primary-700 cursor-pointer hover:text-primary-800">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={catalogUploadingMakerImages || catalogSaving}
                    onChange={async (e) => {
                      await uploadCatalogImages(e.target.files, 'makerImages');
                      e.target.value = '';
                    }}
                  />
                  {catalogUploadingMakerImages ? 'Uploading maker images…' : 'Upload maker image files'}
                </label>
                {!!catalogForm.makerImages.length && (
                  <div className="mt-2 space-y-1 max-h-24 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {catalogForm.makerImages.map((url) => (
                      <div key={url} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="truncate text-gray-700">{url}</span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 shrink-0"
                          onClick={() =>
                            setCatalogForm((f) => ({
                              ...f,
                              makerImages: f.makerImages.filter((u) => u !== url)
                            }))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Model images (optional)
                </label>
                <label className="inline-flex items-center gap-2 mt-2 text-xs text-primary-700 cursor-pointer hover:text-primary-800">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={catalogUploadingModelImages || catalogSaving}
                    onChange={async (e) => {
                      await uploadCatalogImages(e.target.files, 'modelImages');
                      e.target.value = '';
                    }}
                  />
                  {catalogUploadingModelImages ? 'Uploading model images…' : 'Upload model image files'}
                </label>
                {!!catalogForm.modelImages.length && (
                  <div className="mt-2 space-y-1 max-h-24 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {catalogForm.modelImages.map((url) => (
                      <div key={url} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="truncate text-gray-700">{url}</span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 shrink-0"
                          onClick={() =>
                            setCatalogForm((f) => ({
                              ...f,
                              modelImages: f.modelImages.filter((u) => u !== url)
                            }))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Generation (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 5th gen"
                  value={catalogForm.generation}
                  onChange={(e) => setCatalogForm((f) => ({ ...f, generation: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Model year (optional)
                  </label>
                  <input
                    type="number"
                    min={1980}
                    max={2100}
                    placeholder="e.g. 2022"
                    value={catalogForm.modelGenerationYear}
                    onChange={(e) =>
                      setCatalogForm((f) => ({ ...f, modelGenerationYear: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Fuel type (optional)
                  </label>
                  <select
                    value={catalogForm.fuelType}
                    onChange={(e) => setCatalogForm((f) => ({ ...f, fuelType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {CATALOG_FUEL_OPTIONS.map((opt) => (
                      <option key={opt || 'none'} value={opt}>
                        {opt || '— Select —'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Model variant line (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. VX — separate from trim row below"
                  value={catalogForm.modelVariantLine}
                  onChange={(e) =>
                    setCatalogForm((f) => ({ ...f, modelVariantLine: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase pt-1">
                Catalogue trim row
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Generation
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 6th gen"
                    value={catalogForm.trimGeneration}
                    onChange={(e) =>
                      setCatalogForm((f) => ({ ...f, trimGeneration: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Year
                  </label>
                  <input
                    type="number"
                    min={1980}
                    max={2100}
                    required
                    value={catalogForm.year}
                    onChange={(e) => setCatalogForm((f) => ({ ...f, year: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Month
                  </label>
                  <select
                    value={catalogForm.month}
                    onChange={(e) =>
                      setCatalogForm((f) => ({ ...f, month: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {CATALOG_MONTHS.map((mo) => (
                      <option key={mo.value} value={mo.value}>
                        {mo.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Variant / trim
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ZX CVT, VDi"
                  value={catalogForm.variant}
                  onChange={(e) => setCatalogForm((f) => ({ ...f, variant: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  disabled={catalogSaving}
                  onClick={() => setShowAddCatalogModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={catalogSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                >
                  {catalogSaving ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <FaPlus className="text-xs" />
                      Save vehicle
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Vehicle Management
          </h2>
          <p className="text-xs text-gray-600 mt-1 max-w-2xl">
            Manage the <span className="font-semibold">master catalogue</span> by car maker: expand
            a row to see every model, add more models anytime, and use View on a model for year /
            variant lines. Use <span className="font-semibold">Add vehicle</span> to create maker,
            model, and trim in one step, or <span className="font-semibold">Add product</span> to
            list a marketplace part with vehicle fitment from the catalogue.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={openAddProductModal}
            className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 rounded-xl border border-violet-200 bg-violet-50 text-violet-900 text-sm font-semibold shadow-sm hover:bg-violet-100 transition"
          >
            <FaPlus className="text-sm" />
            Add product
          </button>
          <button
            type="button"
            onClick={openAddCatalogModal}
            className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold shadow-sm hover:bg-primary-700 transition"
          >
            <FaPlus className="text-sm" />
            Add vehicle
          </button>
        </div>
      </div>

      <VehicleCatalog
        refreshKey={catalogRefreshKey}
        hideInlineAddForms
        embedded
        hideTitle
      />
    </div>
  );
};

export default VehicleManagement;
